/**
 * App.js – Hauptkomponente der Rundenwettkampf-Webanwendung.
 * Verwaltet Authentifizierung (JWT im localStorage), Navigation und Routing.
 */
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import AppNavbar from './AppNavbar';
import LoginPage from './pages/LoginPage';
import PasswortAendernPage from './pages/PasswortAendernPage';
import DashboardPage from './pages/DashboardPage';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AusstehendeSignaturenModal from './components/AusstehendeSignaturenModal';
import DialogHost from './components/DialogHost';
import { Suspense, useState, useEffect, useCallback, useRef } from 'react';
import { Auth } from './api/apiClient';
import { ROLLE_ADMINISTRATOR } from './constants';
import { clearOfflineSnapshots } from './utils/offlineTeamCache';
import { geraetAbmelden, synchronisiereEinstellungen } from './utils/pushBenachrichtigungen';
import lazyPage, { SeiteLaedt } from './utils/lazyPage';

// Seiten, die nicht jeder Benutzer braucht, werden erst beim Aufruf geladen
// (eigene Chunks). Sofort geladen bleiben Anmeldung, Passwortänderung und das
// Dashboard als Startseite.
const AdminPage = lazyPage(() => import('./pages/Admin/AdminPage'));
const ManagerPage = lazyPage(() => import('./pages/ManagerPage'));
const SchuetzenPage = lazyPage(() => import('./pages/Admin/SchuetzenPage'));
const VereinePage = lazyPage(() => import('./pages/Admin/VereinePage'));
const KlassenEinstellungenPage = lazyPage(() => import('./pages/Admin/KlassenEinstellungenPage'));
const BenutzerEinstellungenPage = lazyPage(() => import('./pages/Admin/BenutzerEinstellungenPage'));
const FunktionstraegerMailPage = lazyPage(() => import('./pages/Admin/FunktionstraegerMailPage'));
const SaisonVerwaltungPage = lazyPage(() => import('./pages/Admin/SaisonVerwaltungPage'));
const SaisonDetailPage = lazyPage(() => import('./pages/Admin/SaisonDetailPage'));
const SaisonFehlendeBerichtePage = lazyPage(() => import('./pages/Admin/SaisonFehlendeBerichtePage'));
const SaisonVereinsMannschaftenPage = lazyPage(() => import('./pages/Admin/SaisonVereinsMannschaftenPage'));
const SaisonMannschaftenSuchePage = lazyPage(() => import('./pages/Admin/SaisonMannschaftenSuchePage'));
const SaisonWettkaempfeSuchePage = lazyPage(() => import('./pages/Admin/SaisonWettkaempfeSuchePage'));
const MannschaftsZuordnungPage = lazyPage(() => import('./pages/Admin/MannschaftsZuordnungPage'));
const VereinsMeldungenPage = lazyPage(() => import('./pages/Admin/VereinsMeldungenPage'));
const KlassenWettkampfPage = lazyPage(() => import('./pages/Admin/KlassenWettkampfPage'));
const KlassenMannschaftenPage = lazyPage(() => import('./pages/Admin/KlassenMannschaftenPage'));
const ProfilPage = lazyPage(() => import('./pages/ProfilPage'));
const ClassResultsPage = lazyPage(() => import('./pages/Admin/ClassResultsPage'));
const ErgebnisEingabePage = lazyPage(() => import('./pages/ErgebnisEingabePage'));
const SchiessmeisterPage = lazyPage(() => import('./pages/Verein/SchiessmeisterPage'));
const VereinSchuetzenPage = lazyPage(() => import('./pages/Verein/VereinSchuetzenPage'));
const SchiessmeisterMannschaftenPage = lazyPage(() => import('./pages/Verein/SchiessmeisterMannschaftenPage'));
const SchiessmeisterOrtePage = lazyPage(() => import('./pages/Verein/SchiessmeisterOrtePage'));
const VereinSaisonMannschaftenPage = lazyPage(() => import('./pages/Verein/VereinSaisonMannschaftenPage'));
const VereinFehlendeBerichtePage = lazyPage(() => import('./pages/Verein/VereinFehlendeBerichtePage'));
const ElektronischeStaendeDatenPage = lazyPage(() => import('./pages/Verein/ElektronischeStaendeDatenPage'));
const MannschaftUebersichtPage = lazyPage(() => import('./pages/Verein/MannschaftUebersichtPage'));
const MannschaftsmeldungSharePage = lazyPage(() => import('./pages/Verein/MannschaftsmeldungSharePage'));
const VereinKalenderPage = lazyPage(() => import('./pages/Verein/VereinKalenderPage'));
const VereinWettkampfverwalterPage = lazyPage(() => import('./pages/Verein/VereinWettkampfverwalterPage'));
const NotFoundPage = lazyPage(() => import('./pages/NotFoundPage'));
const SaisonPage = lazyPage(() => import('./pages/Saison/SaisonPage'));
const FehlendeWettkampfberichtePage = lazyPage(() => import('./pages/Saison/FehlendeWettkampfberichtePage'));
const WettkaempfePage = lazyPage(() => import('./pages/Saison/WettkaempfePage'));
const WettkampfErgebnisPage = lazyPage(() => import('./pages/Saison/WettkampfErgebnisPage'));
const WettkampfWeiterleitungPage = lazyPage(() => import('./pages/Saison/WettkampfWeiterleitungPage'));
const BerichtErgebnissePage = lazyPage(() => import('./pages/Berichte/BerichtErgebnissePage'));
const BerichteDateienPage = lazyPage(() => import('./pages/Berichte/BerichteDateienPage'));
const AdminDateienPage = lazyPage(() => import('./pages/Admin/AdminDateienPage'));
const OeffentlicherDownloadPage = lazyPage(() => import('./pages/OeffentlicherDownloadPage'));
const SicherheitsprotokollPage = lazyPage(() => import('./pages/Admin/SicherheitsprotokollPage'));
const AktiveBenutzerPage = lazyPage(() => import('./pages/Admin/AktiveBenutzerPage'));
const AenderungsprotokollPage = lazyPage(() => import('./pages/Admin/AenderungsprotokollPage'));
const FreigabelinksPage = lazyPage(() => import('./pages/Admin/FreigabelinksPage'));
const GebuehrenPage = lazyPage(() => import('./pages/Admin/GebuehrenPage'));
const StandkonflikteSeite = lazyPage(() => import('./pages/Admin/StandkonflikteSeite'));
const VereinsInfoPage = lazyPage(() => import('./pages/VereinsInfoPage'));
const AllgemeinPage = lazyPage(() => import('./pages/Allgemein/AllgemeinPage'));
const MitteilungenPage = lazyPage(() => import('./pages/Allgemein/MitteilungenPage'));
const KalenderPage = lazyPage(() => import('./pages/Allgemein/KalenderPage'));
const UeberPage = lazyPage(() => import('./pages/Allgemein/UeberPage'));
// Nutzungsbedingungen vorerst deaktiviert
// const NutzungsbedingungenPage = lazyPage(() => import('./pages/Allgemein/NutzungsbedingungenPage'));
const MitteilungenVerwaltungPage = lazyPage(() => import('./pages/Admin/MitteilungenVerwaltungPage'));
const MeineMannschaftenPage = lazyPage(() => import('./pages/MeineMannschaftenPage'));

function ShareLayout({ onLogout }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-600">
            <svg className="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
            </svg>
          </div>
          <span className="text-sm font-semibold text-slate-900">RWK Manager</span>
          <span className="badge badge-amber text-xs">Geteilter Link</span>
        </div>
        <button
          onClick={onLogout}
          className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1.5 rounded-lg px-3 py-1.5 hover:bg-slate-100 transition-colors"
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Sitzung beenden
        </button>
      </header>
      <main>
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <Suspense fallback={<SeiteLaedt />}>
            <Outlet />
          </Suspense>
        </div>
      </main>
    </div>
  );
}

const DEFAULT_SCHIESSMEISTER_SAISON_ID = 2026;

function ClassResultsPageWrapper() {
  const { klasseId } = useParams();
  return <ClassResultsPage klasseId={parseInt(klasseId, 10)} />;
}

function BerichteKlasseRedirect() {
  const { saisonId, klasseId } = useParams();
  return <Navigate to={`/saison/${saisonId}/klasse/${klasseId}`} replace />;
}

function VereinMeldungRedirect() {
  const { id } = useParams();
  return <Navigate to={`/verein/${id}/meldung/${DEFAULT_SCHIESSMEISTER_SAISON_ID}`} replace />;
}

function TeamOverviewLegacyRedirect() {
  const { mannschaftsId } = useParams();
  return <Navigate to={`/mannschaft/${mannschaftsId}`} replace />;
}

function getTokenExp(token) {
  try {
    const payloadPart = token.split('.')[1];
    if (!payloadPart) return null;
    const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

function getTokenClaim(token, claim) {
  try {
    const payloadPart = token.split('.')[1];
    if (!payloadPart) return null;
    const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return payload[claim] ?? null;
  } catch {
    return null;
  }
}

function hasAdminBerechtigung(user) {
  if (Number(user?.rollenId) === ROLLE_ADMINISTRATOR) return true;
  const token = localStorage.getItem('authToken');
  if (!token) return false;
  try {
    const part = token.split('.')[1];
    if (!part) return false;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    const raw = payload['berechtigung'];
    if (!raw) return false;
    const list = Array.isArray(raw) ? raw : [raw];
    return list.some(b => parseInt(String(b).split(':')[0], 10) === ROLLE_ADMINISTRATOR);
  } catch {
    return false;
  }
}

function getDefaultRouteForUser(user) {
  return '/dashboard';
}

function RequireAdmin({ currentUser }) {
  if (!hasAdminBerechtigung(currentUser)) {
    return <Navigate to={getDefaultRouteForUser(currentUser)} replace />;
  }
  return <Outlet />;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [isShareSession, setIsShareSession] = useState(false);
  const [shareVereinsId, setShareVereinsId] = useState(null);
  const [shareSaisonId, setShareSaisonId] = useState(null);
  const [emailReminderDismissed, setEmailReminderDismissed] = useState(
    () => sessionStorage.getItem('emailReminderDismissed') === 'true'
  );
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = useCallback(() => {
    // Push-Abonnement dieses Geräts vom Benutzer lösen (liest das Token vorab).
    geraetAbmelden();
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('emailReminderDismissed');
    clearOfflineSnapshots();
    setIsAuthenticated(false);
    setCurrentUser(null);
    setMustChangePassword(false);
    setIsShareSession(false);
    setShareVereinsId(null);
    setShareSaisonId(null);
    setEmailReminderDismissed(false);
    navigate('/login');
  }, [navigate]);

  const handleDismissEmailReminder = () => {
    sessionStorage.setItem('emailReminderDismissed', 'true');
    setEmailReminderDismissed(true);
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const user = localStorage.getItem('currentUser');
    if (token) {
      const exp = getTokenExp(token);
      if (exp && Date.now() >= exp * 1000) {
        handleLogout();
        return;
      }
      setIsAuthenticated(true);
      const tokenType = getTokenClaim(token, 'token_type');
      if (tokenType === 'share_session') {
        setIsShareSession(true);
        setShareVereinsId(getTokenClaim(token, 'verein_id'));
        setShareSaisonId(getTokenClaim(token, 'saison_id'));
        return;
      }
      const mustChange = getTokenClaim(token, 'passwort_aktualisieren') === 'true';
      setMustChangePassword(mustChange);
      if (user) {
        try {
          setCurrentUser(JSON.parse(user));
        } catch (e) {
          localStorage.removeItem('currentUser');
          setCurrentUser(null);
        }
      }
    }
  }, [handleLogout]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const check = () => {
      const token = localStorage.getItem('authToken');
      if (!token) { handleLogout(); return; }
      const exp = getTokenExp(token);
      if (exp && Date.now() >= exp * 1000) handleLogout();
    };
    check();
    const id = setInterval(check, 60_000);
    return () => clearInterval(id);
  }, [isAuthenticated, handleLogout]);

  // Token beim Seitenaufruf erneuern und Berechtigungen aktualisieren
  useEffect(() => {
    if (!isAuthenticated || isShareSession) return;
    Auth.refresh()
      .then(r => {
        if (r?.access_token) localStorage.setItem('authToken', r.access_token);
        if (r?.user) {
          localStorage.setItem('currentUser', JSON.stringify(r.user));
          setCurrentUser(r.user);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isShareSession]);

  // Push-Benachrichtigungen: Auswahl dieses Geräts erneut an die API melden
  // (Zuordnung zum angemeldeten Benutzer, ggf. neuer Schlüssel).
  useEffect(() => {
    if (!isAuthenticated || isShareSession) return;
    synchronisiereEinstellungen();
  }, [isAuthenticated, isShareSession]);

  // Aktivitätserkennung für Sitzungen ohne "Angemeldet bleiben" (2-Stunden-Timer zurücksetzen)
  const lastActivityRefreshRef = useRef(0);
  useEffect(() => {
    if (!isAuthenticated || isShareSession) return;
    if (localStorage.getItem('rememberMe') === 'true') return;

    const THROTTLE_MS = 5 * 60 * 1000;
    const onActivity = () => {
      if (Date.now() - lastActivityRefreshRef.current < THROTTLE_MS) return;
      lastActivityRefreshRef.current = Date.now();
      Auth.refresh()
        .then(r => {
          if (r?.access_token) localStorage.setItem('authToken', r.access_token);
          if (r?.user) {
            localStorage.setItem('currentUser', JSON.stringify(r.user));
            setCurrentUser(r.user);
          }
        })
        .catch(() => {});
    };

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, onActivity));
  }, [isAuthenticated, isShareSession]);

  const handleLoginSuccess = (user, passwortAktualisieren = false) => {
    setIsAuthenticated(true);
    setCurrentUser(user);
    setMustChangePassword(passwortAktualisieren);
  };

  const handlePasswordChanged = () => {
    const stored = localStorage.getItem('currentUser');
    let user = null;
    if (stored) {
      try {
        user = JSON.parse(stored);
        setCurrentUser(user);
      } catch {
        // ignore
      }
    }
    setMustChangePassword(false);
    navigate(getDefaultRouteForUser(user));
  };

  if (!isAuthenticated) {
    if (location.pathname.startsWith('/share/')) {
      return (
        <Routes>
          <Route path="/share/meldung" element={<MannschaftsmeldungSharePage />} />
          <Route path="*" element={<LoginPage onLoginSuccess={handleLoginSuccess} />} />
        </Routes>
      );
    }
    if (location.pathname.startsWith('/download/')) {
      return (
        <Routes>
          <Route path="/download/:key" element={<OeffentlicherDownloadPage />} />
          <Route path="*" element={<LoginPage onLoginSuccess={handleLoginSuccess} />} />
        </Routes>
      );
    }
    // Nutzungsbedingungen vorerst deaktiviert
    // if (location.pathname === '/nutzungsbedingungen') {
    //   return <NutzungsbedingungenPage oeffentlich />;
    // }
    const redirectTo = location.pathname !== '/login' && location.pathname !== '/'
      ? location.pathname + location.search
      : null;
    return <LoginPage onLoginSuccess={handleLoginSuccess} redirectTo={redirectTo} />;
  }

  if (mustChangePassword) {
    return <PasswortAendernPage onPasswordChanged={handlePasswordChanged} />;
  }

  if (isShareSession && shareVereinsId && shareSaisonId) {
    return (
      <Routes>
        <Route element={<ShareLayout onLogout={handleLogout} />}>
          <Route path="/verein/:id/meldung/:saisonId" element={<SchiessmeisterMannschaftenPage />} />
          <Route path="/mannschaft/:mannschaftsId" element={<MannschaftUebersichtPage />} />
          <Route path="/verein/:id/mannschaft/:mannschaftsId/saison/:saisonId" element={<TeamOverviewLegacyRedirect />} />
        </Route>
        <Route path="/download/:key" element={<OeffentlicherDownloadPage />} />
        <Route path="*" element={<Navigate to={`/verein/${shareVereinsId}/meldung/${shareSaisonId}`} replace />} />
      </Routes>
    );
  }

  return (
    <>
      <AusstehendeSignaturenModal currentUser={currentUser} />
      <Routes>
      <Route path="/login" element={<LoginPage onLoginSuccess={handleLoginSuccess} />} />
      <Route path="/passwort-aendern" element={<PasswortAendernPage onPasswordChanged={handlePasswordChanged} />} />
      <Route element={
        <AppNavbar
          currentUser={currentUser}
          onLogout={handleLogout}
          showEmailReminder={isAuthenticated && !!currentUser && !currentUser?.email && !emailReminderDismissed}
          onDismissEmailReminder={handleDismissEmailReminder}
        />
      }>
        <Route path="/" element={
          <Navigate to={getDefaultRouteForUser(currentUser)} replace />
        } />

        {/* Profil */}
        <Route path="dashboard" element={<DashboardPage currentUser={currentUser} />} />
        <Route path="profil" element={<ProfilPage currentUser={currentUser} onUserUpdated={setCurrentUser} />} />
        <Route path="mannschaft/:mannschaftsId" element={<MannschaftUebersichtPage />} />
        <Route path="meine-mannschaften" element={<MeineMannschaftenPage currentUser={currentUser} />} />

        {/* Admin-Bereich */}
        <Route path="admin" element={<RequireAdmin currentUser={currentUser} />}>
          <Route index element={<AdminPage />} />
          <Route path="vereine" element={<VereinePage />} />
          <Route path="schuetzen" element={<SchuetzenPage />} />
          <Route path="klasseneinstellungen" element={<KlassenEinstellungenPage />} />
          <Route path="benutzereinstellungen" element={<BenutzerEinstellungenPage />} />
          <Route path="funktionstraeger-mail" element={<FunktionstraegerMailPage />} />
          <Route path="dateien" element={<AdminDateienPage />} />
          <Route path="mitteilungen" element={<MitteilungenVerwaltungPage />} />
          <Route path="gebuehren" element={<GebuehrenPage />} />
          <Route path="sicherheitsprotokoll" element={<SicherheitsprotokollPage />} />
          <Route path="aktive-benutzer" element={<AktiveBenutzerPage />} />
          <Route path="aenderungsprotokoll" element={<AenderungsprotokollPage />} />
          <Route path="freigabelinks" element={<FreigabelinksPage />} />
          <Route path="saisons">
            <Route index element={<SaisonVerwaltungPage />} />
            <Route path=":saisonId" element={<SaisonDetailPage />} />
            <Route path=":saisonId/klassen">
              <Route path=":klassenId/wettkampf" element={<KlassenWettkampfPage />} />
              <Route path=":klassenId/mannschaften" element={<KlassenMannschaftenPage />} />
            </Route>
            <Route path=":saisonId/fehlende-berichte" element={<SaisonFehlendeBerichtePage />} />
            <Route path=":saisonId/vereinsmannschaften" element={<SaisonVereinsMannschaftenPage />} />
            <Route path=":saisonId/mannschaften" element={<SaisonMannschaftenSuchePage />} />
            <Route path=":saisonId/wettkaempfe" element={<SaisonWettkaempfeSuchePage />} />
            <Route path=":saisonId/standkonflikte" element={<StandkonflikteSeite />} />
            <Route path=":saisonId/vereinsmeldungen" element={<VereinsMeldungenPage />} />
            <Route path=":saisonId/klassengruppen/:klassengruppenId/mannschaften" element={<MannschaftsZuordnungPage />} />
          </Route>
        </Route>

        {/* Verein / Schießmeister-Bereich */}
        <Route path="verein">
          <Route index element={
            currentUser?.vereine?.[0]?.id
              ? <Navigate to={`/verein/${currentUser.vereine[0].id}`} replace />
              : <Navigate to="/saison" replace />
          } />
          <Route path=":id">
            <Route index element={<SchiessmeisterPage />} />
            <Route path="schuetzen" element={<VereinSchuetzenPage />} />
            <Route path="wettkampfverwalter" element={<VereinWettkampfverwalterPage />} />
            <Route path="kalender" element={<VereinKalenderPage />} />
            <Route path="orte" element={<SchiessmeisterOrtePage />} />
            <Route path="saison-mannschaften" element={<VereinSaisonMannschaftenPage />} />
            <Route path="fehlende-berichte" element={<VereinFehlendeBerichtePage />} />
            <Route path="elektronische-staende" element={<ElektronischeStaendeDatenPage />} />
            <Route path="mannschaft/:mannschaftsId/saison/:saisonId" element={<TeamOverviewLegacyRedirect />} />
            <Route path="meldung">
              <Route index element={<VereinMeldungRedirect />} />
              <Route path=":saisonId" element={<SchiessmeisterMannschaftenPage />} />
            </Route>
            <Route path="mannschaften" element={<VereinMeldungRedirect />} />
          </Route>
        </Route>

        {/* Wettkampfmanager-Bereich (in Saison aufgegangen) */}
        <Route path="wettkampfmanager" element={<Navigate to="/saison" replace />} />

        {/* Sonstige Seiten */}
        <Route path="manager" element={<ManagerPage />} />
        <Route path="saison">
          <Route index element={<SaisonPage />} />
          <Route path=":saisonId" element={<SaisonPage />} />
          <Route path=":saisonId/fehlende-berichte" element={<FehlendeWettkampfberichtePage />} />
          <Route path=":saisonId/klasse/:klasseId" element={<BerichtErgebnissePage />} />
          <Route path=":saisonId/klasse/:klasseId/wettkaempfe" element={<WettkaempfePage />} />
          <Route path=":saisonId/wettkampf/:wettkampfNummer" element={<WettkampfWeiterleitungPage />} />
          <Route path=":saisonId/wettkampf/:wettkampfNummer/ergebnis" element={<WettkampfErgebnisPage />} />
          <Route path=":saisonId/wettkampf/:wettkampfNummer/ergebniseingabe" element={<ErgebnisEingabePage />} />
          {/* Alte URL mit Klassen-Segment – bleibt als Alias erhalten (Lesezeichen, alte Links) */}
          <Route path=":saisonId/klasse/:klasseId/wettkampf/:wettkampfId/ergebnis" element={<WettkampfErgebnisPage />} />
          <Route path=":saisonId/klasse/:klasseId/wettkampf/:wettkampfId/ergebniseingabe" element={<ErgebnisEingabePage />} />
          <Route path="dateien" element={<Navigate to="/allgemein/downloads" replace />} />
        </Route>
        {/* Allgemein-Bereich */}
        <Route path="allgemein">
          <Route index element={<AllgemeinPage />} />
          <Route path="mitteilungen" element={<MitteilungenPage />} />
          <Route path="kalender" element={<KalenderPage />} />
          <Route path="downloads" element={<BerichteDateienPage />} />
          <Route path="vereinsinfo" element={<VereinsInfoPage />} />
          <Route path="vereinsinfo/:vereinsId" element={<VereinsInfoPage />} />
          <Route path="ueber" element={<UeberPage />} />
          {/* Nutzungsbedingungen vorerst deaktiviert
          <Route path="nutzungsbedingungen" element={<NutzungsbedingungenPage />} /> */}
        </Route>
        {/* <Route path="nutzungsbedingungen" element={<Navigate to="/allgemein/nutzungsbedingungen" replace />} /> */}
        {/* Weiterleitungen für alte Routen */}
        <Route path="berichte" element={<Navigate to="/saison" replace />} />
        <Route path="berichte/dateien" element={<Navigate to="/allgemein/downloads" replace />} />
        <Route path="berichte/:saisonId/klasse/:klasseId" element={<BerichteKlasseRedirect />} />
        <Route path="vereinsinfo" element={<Navigate to="/allgemein/vereinsinfo" replace />} />
        <Route path="klasse/:klasseId" element={<ClassResultsPageWrapper />} />
      </Route>

      <Route path="/share/meldung" element={<MannschaftsmeldungSharePage />} />
      <Route path="/download/:key" element={<OeffentlicherDownloadPage />} />
      <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}

function AppWithRouter() {
  return (
    <Router>
      {/* Äußerer Rahmen für nachgeladene Seiten ohne Navigationsleiste (Anmeldung
          per Freigabelink, öffentlicher Download, 404). Innerhalb der
          Navigationsleiste greift der Rahmen um deren <Outlet />. */}
      <Suspense fallback={<SeiteLaedt />}>
        <App />
      </Suspense>
      <DialogHost />
    </Router>
  );
}

export default AppWithRouter;
